import {
  teamGroups,
  withCurrent,
  applyStartTime,
  calcDur,
  fmt,
  getAllTypes,
  getPlanStart,
  getType,
  appendPeriod,
  earliestStart,
  editPeriod,
  insertPeriod,
  movePeriod,
  nextPeriodTime,
  periodDurationValue,
  removePeriod,
  kvGet,
  assignmentLines,
  type Assignment,
  type DrillData,
  type LibraryItemRow,
  type Period,
  type ScriptLib,
} from '@coach/core';
import { Badge, ChipToggle, Modal, useConfirm, useToast } from '@coach/ui';
import { Fragment, useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { useApp } from '../../lib/app';
import { usePlanStore } from './liveSession';
import { SavedPlans } from '../saved/SavedView';
import { useLocation, useNavigate } from 'react-router-dom';
import { armAudio } from './audio';
import { DrillDiagram } from '../drills/DrillDiagram';
import { TextZoom } from './TextZoom';
import { RunScript } from '../scripts/RunScript';
import { useScriptLib } from '../scripts/useScriptLib';

import { CAN_PRINT, esc, printHtml } from '../../lib/print';
import './plan.css';

function normalizeAssignments(p: Period & { drillId?: string; customActivity?: string; notes?: string; groups?: string[] }): Assignment[] {
  if (Array.isArray(p.assignments)) return p.assignments;
  if (p.drillId || p.customActivity || p.notes || p.groups?.length) {
    return [{ drillId: p.drillId || '', customActivity: p.customActivity || '', groups: p.groups || [], notes: p.notes || '' }];
  }
  return [];
}

/** Breaks usually carry nothing; an empty one just shows its name, no set-up prompt. */
const BREAK_TYPES = new Set(['Water', 'EOWO']);

/** Nearest ancestor that actually scrolls, or null when the page itself does. */
function scrollParent(el: HTMLElement): HTMLElement | null {
  for (let n = el.parentElement; n && n !== document.body && n !== document.documentElement; n = n.parentElement) {
    const o = getComputedStyle(n).overflowY;
    if ((o === 'auto' || o === 'scroll') && n.scrollHeight > n.clientHeight) return n;
  }
  return null;
}

export function PlanView() {
  const app = useApp();
  const store = usePlanStore();
  const toast = useToast();
  const { plan, mode, timer, view, session, posted } = store;
  const isRun = mode === 'run';
  const [drills, setDrills] = useState<LibraryItemRow<DrillData>[]>([]);
  const scripts = useScriptLib();
  const [editIdx, setEditIdx] = useState<number | null | 'new'>(null);
  const [detailIdx, setDetailIdx] = useState<number | null>(null);
  // Set while the "new period" editor was opened from a ＋ between two periods.
  const [insertAt, setInsertAt] = useState<number | null>(null);
  const [drag, setDrag] = useState<{ from: number; to: number } | null>(null);
  const dragEndedAt = useRef(0);
  const listRef = useRef<HTMLDivElement>(null);
  const loc = useLocation();
  const nav = useNavigate();
  // Old /saved links (and the removed Saved tab) land here with the sheet open.
  const [savedOpen, setSavedOpen] = useState(() => !!(loc.state as { openSaved?: boolean } | null)?.openSaved);
  useEffect(() => {
    if ((loc.state as { openSaved?: boolean } | null)?.openSaved) nav('/plan', { replace: true, state: null });
  }, [loc.state, nav]);
  // "Edit drill" from the period editor's preview goes to the drill editor and
  // comes back here with the same period's editor open again.
  useEffect(() => {
    const idx = (loc.state as { editIdx?: number } | null)?.editIdx;
    if (typeof idx !== 'number') return;
    setEditIdx(idx);
    nav('/plan', { replace: true, state: null });
  }, [loc.state, nav]);
  const [renaming, setRenaming] = useState(false);
  const [renameVal, setRenameVal] = useState('');
  const [renameShell, setRenameShell] = useState(false);
  const [saveAs, setSaveAs] = useState<{ name: string; shell: boolean } | null>(null);
  const openRename = () => { setRenameVal(plan.name); setRenameShell(plan.shell === true); setRenaming(true); };
  const applyRename = () => {
    const n = renameVal.trim();
    if (n) store.setPlan((p) => ({ ...p, name: n, shell: plan._canEdit && renameShell ? true : undefined }));
    setRenaming(false);
  };
  const doSaveAs = async () => {
    if (!saveAs) return;
    if (await store.saveAsNew(saveAs.name, { shell: saveAs.shell })) setSaveAs(null);
  };
  const ss = store.saveState;
  const offlineish = app.syncStatus.pending > 0 && app.syncStatus.state !== 'synced' && app.syncStatus.state !== 'pending';
  const saveLabel = !plan._canEdit ? '' : ss.kind === 'saving' ? 'Saving…' : ss.kind === 'saved' ? (offlineish ? '✓ Saved on this phone' : '✓ Saved') : ss.kind === 'error' ? '⚠ ' + ss.message : '';
  const [soundOn, setSoundOn] = useState(false);
  useEffect(() => { void kvGet<boolean>('sound_cues').then((v) => setSoundOn(v === true)); }, []);

  useEffect(() => {
    void app.repos.library.listLive().then((items) => setDrills(items.filter((i) => i.kind === 'drill') as LibraryItemRow<DrillData>[]));
  }, [app.repos.library, app.syncStatus.lastSyncAt]);

  const types = useMemo(() => getAllTypes(app.settings.period_types ?? [], app.settings.hidden_builtins ?? []), [app.settings]);
  const drillName = (id: string) => drills.find((d) => d.id === id)?.data.name || '';
  const canEdit = !isRun && plan._canEdit;
  const canEditDrill = (d: LibraryItemRow<DrillData>) => app.isHead || d.owner_id === app.userId;
  // Reordering re-times the whole plan, so not while a practice is live.
  const canArrange = canEdit && !timer.running;
  const closeEditor = () => { setEditIdx(null); setInsertAt(null); };
  const inserting = editIdx === 'new' && insertAt !== null;
  /**
   * Where the period in the editor lands: a ＋ slot, the end, or its own row.
   * Every path keeps the schedule end to end — nothing is ever re-sorted by
   * time, so periods can't overlap or swap places (Oct 13 2026).
   */
  const placePeriod = (cur: typeof plan, p: Period): { plan: typeof plan; error?: string } => {
    if (editIdx === 'new') return { plan: { ...cur, ...(insertAt !== null ? insertPeriod(cur, p, insertAt) : appendPeriod(cur, p)) } };
    const r = editPeriod(cur, editIdx as number, p);
    return { plan: { ...cur, ...r.plan }, error: r.error };
  };
  /** Save from the editor; false (and a toast) when the start isn't allowed. */
  const commitPeriod = (p: Period): boolean => {
    const r = placePeriod(plan, p);
    if (r.error) { toast(r.error); return false; }
    store.setPlan((cur) => placePeriod(cur, p).plan);
    return true;
  };

  // Drag a period by its grip. The row stays put (dimmed) and a gold line shows
  // the slot; on release the list is re-timed from the practice start.
  const startDrag = (e: ReactPointerEvent, from: number) => {
    const list = listRef.current;
    if (e.button > 0 || drag || !list) return;
    e.preventDefault();
    e.stopPropagation();
    const sc = scrollParent(list);
    let y = e.clientY, to = from, raf = 0;
    const place = () => {
      const rows = list.querySelectorAll<HTMLElement>('.period-row');
      let t = rows.length;
      for (let k = 0; k < rows.length; k++) {
        const r = rows[k]!.getBoundingClientRect();
        if (y < r.top + r.height / 2) { t = k; break; }
      }
      if (t !== to) { to = t; setDrag({ from, to }); }
    };
    const tick = () => {
      const vh = window.innerHeight, edge = 70;
      const d = y < edge ? -Math.ceil((edge - y) / 6) : y > vh - edge ? Math.ceil((y - (vh - edge)) / 6) : 0;
      if (d) { if (sc) sc.scrollTop += d; else window.scrollBy(0, d); place(); }
      raf = requestAnimationFrame(tick);
    };
    const move = (ev: PointerEvent) => { y = ev.clientY; place(); };
    const end = (ev: PointerEvent) => {
      document.removeEventListener('pointermove', move);
      document.removeEventListener('pointerup', end);
      document.removeEventListener('pointercancel', end);
      cancelAnimationFrame(raf);
      document.body.style.userSelect = '';
      dragEndedAt.current = Date.now();
      setDrag(null);
      if (ev.type === 'pointercancel' || to === from || to === from + 1) return;
      store.setPlan((cur) => ({ ...cur, ...movePeriod(cur, from, to) }));
      toast('Period moved — times updated');
    };
    document.body.style.userSelect = 'none';
    setDrag({ from, to });
    document.addEventListener('pointermove', move);
    document.addEventListener('pointerup', end);
    document.addEventListener('pointercancel', end);
    raf = requestAnimationFrame(tick);
  };
  const dropAt = drag && drag.to !== drag.from && drag.to !== drag.from + 1 ? drag.to : -1;

  const onStart = (v: string) => {
    if (isRun) return;
    const { plan: next, delta } = applyStartTime(plan, v);
    store.setPlan((p) => ({ ...p, ...next }));
    if (delta) toast(`Practice shifted ${delta > 0 ? '+' : '−'}${Math.abs(delta)} min`);
  };

  const printPlan = () => {
    const rows = plan.periods.map((p, i) => {
      const t = getType(p.type, app.settings.period_types ?? [], app.settings.hidden_builtins ?? []);
      const a = normalizeAssignments(p).map((x) => `<div><b>${esc(x.customActivity || drillName(x.drillId) || '—')}</b>${x.groups.length ? ' ' + x.groups.map((g) => `<span class="chip">${esc(g)}</span>`).join('') : ''}${x.notes ? `<div class="muted">${esc(x.notes)}</div>` : ''}</div>`).join('');
      return `<tr><td style="white-space:nowrap;font-family:monospace;font-weight:bold">${esc(fmt(p.time))}</td><td><span class="badge" style="background:${t.bg};color:${t.fg}">${esc(t.code)}</span> ${esc(t.label)}</td><td>${a}</td><td>${esc(calcDur(plan.periods, i))}</td></tr>`;
    }).join('');
    const ok = printHtml(plan.name, `<h1>${esc(plan.name)}</h1><div class="muted">${esc(app.team.name)} · start ${esc(getPlanStart(plan))}${plan.offFocus ? ' · O: ' + esc(plan.offFocus) : ''}${plan.defFocus ? ' · D: ' + esc(plan.defFocus) : ''}</div><table><thead><tr><th>Time</th><th>Period</th><th>Assignments</th><th>Len</th></tr></thead><tbody>${rows}</tbody></table>`);
    if (!ok) toast('Pop-up blocked — allow pop-ups to print');
  };

  const stopBtn = () => {
    // Assistants follow; a head coach following can still end the practice (two taps).
    if (session.following && !app.isHead) return <button className="timer-btn timer-btn-follow" disabled>🔴 Live</button>;
    if (!timer.running) return <button className="timer-btn timer-btn-start" onClick={() => void store.onStopTap()}>▶ Start</button>;
    if (timer.stopArmed) return <button className="timer-btn timer-btn-armed" onClick={() => void store.onStopTap()}>■ Tap again to stop</button>;
    return <button className="timer-btn timer-btn-stop" onClick={() => void store.onStopTap()}>■ Stop</button>;
  };

  return (
    <div>
      {posted.banner && (
        <div className="posted-banner">
          <div className="grow">
            📌 <strong>Today’s Plan</strong> {posted.banner.kind === 'updated' ? 'was updated' : 'is posted'}: {posted.banner.info.plan_name}
          </div>
          <button className="btn btn-sm btn-primary" onClick={() => void store.openPosted()}>Open</button>
          <button className="btn btn-sm btn-ghost" onClick={store.dismissBanner}>Not now</button>
        </div>
      )}
      {plan.shell && plan._id && plan._canEdit && !isRun && (
        <div className="posted-banner shell-banner">
          <div className="grow">🧩 <strong>Editing the “{plan.name}” shell.</strong> Changes here carry into every practice started from it. To plan a practice, open the shell from 📁 Plans — that starts a dated copy.</div>
        </div>
      )}
      {!plan._canEdit && plan._id && (
        <div className="posted-banner" style={{ background: '#2a2a24' }}>
          <div className="grow">🔒 Read-only — this plan belongs to {plan._owner || 'another coach'}. Saving makes your own copy.</div>
        </div>
      )}

      <div className="card card-hero" style={{ marginBottom: 0 }}>
        <div className="row">
          <div className="day-label grow" onClick={openRename} title="Tap to rename">
            {plan.shell && <span className="shell-chip" title="Practice shell">🧩</span>}
            {plan.name}
            {posted.info && (
              <span className="posted-badge" title={`Today's Plan: ${posted.info.plan_name}${posted.info.posted_by_name ? ' · posted by ' + posted.info.posted_by_name : ''} — tap to open`} onClick={(e) => { e.stopPropagation(); void store.openPosted(); }}>
                📌 TODAY
              </span>
            )}
          </div>
          <input type="time" className="start-time" value={getPlanStart(plan)} disabled={isRun} onChange={(e) => onStart(e.target.value)} />
          {app.isHead && (
            <button className={'small-btn' + (posted.isThisPlan ? ' posted-on' : '')} onClick={() => void store.togglePost()} title={posted.isThisPlan ? 'Posted as Today’s Plan — tap to take it down' : 'Post as Today’s Plan'}>
              📌
            </button>
          )}
          <button className={'small-btn mode-btn ' + (isRun ? 'mode-edit' : 'mode-run')} onClick={() => void store.toggleMode()}>
            {isRun ? '✏️ Edit' : '▶ Run'}
          </button>
        </div>
        <div className="row" style={{ marginTop: 8, gap: 6 }}>
          <input placeholder="Offense focus" value={plan.offFocus} readOnly={isRun} style={{ opacity: isRun ? 0.6 : 1 }} onChange={(e) => store.setPlan((p) => ({ ...p, offFocus: e.target.value }))} />
          <input placeholder="Defense focus" value={plan.defFocus} readOnly={isRun} style={{ opacity: isRun ? 0.6 : 1 }} onChange={(e) => store.setPlan((p) => ({ ...p, defFocus: e.target.value }))} />
        </div>
        <div className="row" style={{ marginTop: 8, gap: 6 }}>
          {isRun && stopBtn()}
          {isRun && !timer.running && <button className="small-btn" onClick={() => setSavedOpen(true)}>📁 Plans</button>}
          {!isRun && (
            <>
              <button className="small-btn" onClick={() => setSavedOpen(true)} title="Saved plans — open, copy, folders, trash">📁 Plans</button>
              <button className="small-btn" onClick={() => setSaveAs({ name: plan.name + ' (copy)', shell: false })} title="Save a copy under a new name — or as a shell">⧉ Save as</button>
              <button className="small-btn" onClick={() => store.newPlan()} title="Start a blank plan (this one is already saved)">＋ New</button>
              {CAN_PRINT && <button className="small-btn print-only" onClick={printPlan} title="Print">🖨</button>}
            </>
          )}
          <div className="grow small" style={{ textAlign: 'right', opacity: 0.85 }}>
            {timer.running ? view.status : isRun ? (plan.periods.length ? `Tap a time to choose start · ▶ Start` : '⏱ Timer ready') : session.note || (saveLabel && ss.kind !== 'error' ? <span className="save-status">{saveLabel}</span> : '✏️ Edit Mode')}
          </div>
        </div>
        {!isRun && ss.kind === 'error' && plan._canEdit && <div className="save-status save-error small" style={{ marginTop: 8 }}>{saveLabel}</div>}
        {app.isHead && soundOn && !store.audioArmed && posted.info && !timer.running && (
          <div className="audio-hint" onClick={armAudio}>🔇 Tap anywhere once so the sound cues can play</div>
        )}
        {session.note && timer.running && <div className="small" style={{ marginTop: 6, opacity: 0.85 }}>{session.note}</div>}
      </div>

      {timer.running && (
        <div className={'timer-banner' + (view.warning ? ' warning' : '')}>
          <div className="timer-period-name">{view.periodName}</div>
          <div className="timer-text">{view.text}</div>
          <div className="timer-progress-track"><div className="timer-progress-fill" style={{ width: view.progressPct + '%' }} /></div>
        </div>
      )}

      <div className="period-list" ref={listRef}>
        {plan.periods.length === 0 && <div className="empty">No periods yet. {canEdit ? 'Tap + to add the first one.' : ''}</div>}
        {plan.periods.map((p, i) => {
          const t = getType(p.type, app.settings.period_types ?? [], app.settings.hidden_builtins ?? []);
          const dur = calcDur(plan.periods, i);
          const assignments = normalizeAssignments(p);
          const active = timer.running && i === view.periodIdx;
          const past = timer.running && i < view.periodIdx;
          const selectable = isRun && !timer.running;
          const selected = selectable && i === store.startFromIdx;
          return (
            <Fragment key={i}>
            {canArrange && (
              <div className={'period-insert' + (i === 0 ? ' first' : '')}>
                <button type="button" title="Insert a period here" aria-label={`Insert a period before ${fmt(p.time)}`} onClick={() => { setInsertAt(i); setEditIdx('new'); }}>＋</button>
              </div>
            )}
            {dropAt === i && <div className="period-dropline" />}
            <div className={'period-row' + (p.type === 'Water' ? ' water-break' : '') + (active ? ' active-period' : '') + (past ? ' past' : '') + (drag?.from === i ? ' dragging' : '')} onClick={() => { if (Date.now() - dragEndedAt.current < 400) return; if (isRun) setDetailIdx(i); else if (canEdit) setEditIdx(i); }} style={{ cursor: isRun || canEdit ? 'pointer' : 'default' }}>
              {canArrange && <div className="period-grip" title="Drag to reorder" aria-label="Drag to reorder" onClick={(e) => e.stopPropagation()} onPointerDown={(e) => startDrag(e, i)}>⋮⋮</div>}
              <div className={'period-time' + (selectable ? ' run-selectable' : '') + (selected ? ' start-from' : '')} onClick={(e) => { if (selectable) { e.stopPropagation(); store.setStartFromIdx(i); toast(`▶ Start from ${fmt(p.time)}`); } }}>
                {fmt(p.time)}
              </div>
              <div className="period-badge"><Badge bg={t.bg} fg={t.fg}>{t.code}</Badge></div>
              <div className="period-content">
                {assignments.length === 0 && (BREAK_TYPES.has(p.type)
                  ? <div className="period-drill unset break">{t.label}</div>
                  : <div className="period-drill unset">{canEdit && !isRun ? '— tap to set up —' : t.label}</div>)}
                {assignments.map((a, j) => {
                  const name = a.customActivity || drillName(a.drillId);
                  return (
                    <div key={j} className="assignment-row">
                      <div className={'period-drill' + (name ? '' : BREAK_TYPES.has(p.type) ? ' unset break' : ' unset')}>{name || (BREAK_TYPES.has(p.type) ? t.label : '— tap to add —')}</div>
                      {a.groups.length > 0 && <div className="period-groups">{a.groups.map((g) => <span key={g} className="chip">{g}</span>)}</div>}
                      {a.notes && <div className="period-notes">{a.notes.length > 60 ? a.notes.slice(0, 60) + '…' : a.notes}</div>}
                    </div>
                  );
                })}
              </div>
              {dur && <div className="period-duration">{dur}</div>}
            </div>
            </Fragment>
          );
        })}
        {dropAt === plan.periods.length && <div className="period-dropline" />}
      </div>

      {canEdit && <button className="fab" onClick={() => { setInsertAt(null); setEditIdx('new'); }} title="Add period at the end">＋</button>}

      {editIdx !== null && (
        <PeriodModal
          period={editIdx === 'new' ? null : plan.periods[editIdx]!}
          insert={inserting}
          defaultTime={inserting && plan.periods[insertAt!] ? fmt(plan.periods[insertAt!]!.time) : nextPeriodTime(plan)}
          defaultDuration={editIdx === 'new' ? 8 : periodDurationValue(plan.periods, editIdx)}
          startLock={inserting ? 'Set by where you are inserting — later periods slide back' : editIdx === 'new' && plan.periods.length ? 'New periods go on the end — use ＋ between periods to put one earlier' : undefined}
          minStart={editIdx === 'new' ? null : earliestStart(plan, editIdx)}
          hasLater={editIdx !== 'new' && editIdx < plan.periods.length - 1}
          isFirst={editIdx === 0}
          types={types}
          drills={drills}
          scripts={scripts.lib}
          canEditDrill={canEditDrill}
          onClose={closeEditor}
          onSave={(p) => {
            if (!commitPeriod(p)) return;
            if (inserting) toast(`Inserted — later periods moved back ${p.duration} min`);
            closeEditor();
          }}
          onEditDrill={(p, drillId) => {
            // Keep what was typed: save the period, then come back to its editor.
            // The period's row: its own when editing, else the slot or the end.
            const at = editIdx === 'new' ? (insertAt ?? plan.periods.length) : (editIdx as number);
            if (!commitPeriod(p)) return;
            closeEditor();
            nav('/drills', { state: { editId: drillId, back: { to: '/plan', state: at >= 0 ? { editIdx: at } : null } } });
          }}
          onDelete={editIdx === 'new' ? undefined : () => { store.setPlan((cur) => ({ ...cur, ...removePeriod(cur, editIdx) })); closeEditor(); if (editIdx < plan.periods.length - 1) toast('Deleted — later periods moved up'); }}
        />
      )}

      <Modal open={detailIdx !== null} onClose={() => setDetailIdx(null)} closeButton title={detailIdx !== null && plan.periods[detailIdx] ? `${fmt(plan.periods[detailIdx]!.time)} · ${getType(plan.periods[detailIdx]!.type, app.settings.period_types ?? [], app.settings.hidden_builtins ?? []).label}` : ''}>
        {detailIdx !== null && plan.periods[detailIdx] && <PeriodSheet assignments={normalizeAssignments(plan.periods[detailIdx]!)} drills={drills} scripts={scripts.lib} />}
      </Modal>

      <Modal open={renaming} onClose={() => setRenaming(false)} title="Rename plan" actions={<><button className="btn" onClick={() => setRenaming(false)}>Cancel</button><button className="btn btn-primary" onClick={applyRename}>Rename</button></>}>
        <input value={renameVal} onChange={(e) => setRenameVal(e.target.value)} autoFocus onKeyDown={(e) => { if (e.key === 'Enter') applyRename(); }} />
        {plan._canEdit && !isRun && (
          <label className="row small" style={{ marginTop: 10, gap: 8 }}>
            <input type="checkbox" style={{ width: 'auto' }} checked={renameShell} onChange={(e) => setRenameShell(e.target.checked)} />
            <span>🧩 Practice shell — opening it from Plans starts a dated copy</span>
          </label>
        )}
      </Modal>
      <Modal open={!!saveAs} onClose={() => setSaveAs(null)} title="Save as a new plan" actions={<><button className="btn" onClick={() => setSaveAs(null)}>Cancel</button><button className="btn btn-primary" onClick={() => void doSaveAs()}>Save</button></>}>
        <input value={saveAs?.name ?? ''} onChange={(e) => setSaveAs((v) => (v ? { ...v, name: e.target.value } : v))} autoFocus onKeyDown={(e) => { if (e.key === 'Enter') void doSaveAs(); }} placeholder="Plan name" />
        <label className="row small" style={{ marginTop: 10, gap: 8 }}>
          <input type="checkbox" style={{ width: 'auto' }} checked={saveAs?.shell ?? false} onChange={(e) => setSaveAs((v) => (v ? { ...v, shell: e.target.checked } : v))} />
          <span>🧩 Save it as a practice shell</span>
        </label>
        <div className="muted small" style={{ marginTop: 8 }}>The copy opens here; “{plan.name}” stays as it is.</div>
      </Modal>
      <Modal open={savedOpen} onClose={() => setSavedOpen(false)} title="Saved plans">
        <SavedPlans onLoaded={() => setSavedOpen(false)} />
      </Modal>
    </div>
  );
}

/** What a coach sees when he taps a period in Run mode. Also the editor's Preview. */
function PeriodSheet({ assignments, drills, scripts, editDrill }: {
  assignments: Assignment[];
  drills: LibraryItemRow<DrillData>[];
  scripts: ScriptLib;
  editDrill?: (d: LibraryItemRow<DrillData>) => (() => void) | undefined;
}) {
  return (
    <TextZoom>
      {assignments.map((a, j) => {
        const d = drills.find((x) => x.id === a.drillId);
        const { lines, asRun } = assignmentLines(a, d?.data, scripts);
        const edit = d && editDrill?.(d);
        return (
          <div key={j} className="card" style={{ margin: '0 0 10px' }}>
            <div style={{ fontWeight: 'bold', fontSize: '1.1em' }}>{a.customActivity || d?.data.name || '—'}</div>
            {a.groups.length > 0 && <div style={{ marginTop: 4 }}>{a.groups.map((g) => <span key={g} className="chip on">{g}</span>)}</div>}
            {a.notes && <div className="muted" style={{ marginTop: 6, whiteSpace: 'pre-wrap' }}>{a.notes}</div>}
            {d?.data.desc && <div style={{ marginTop: 6, whiteSpace: 'pre-wrap', fontSize: '0.9em' }}>{d.data.desc}</div>}
            {lines.length > 0 && <div style={{ fontSize: '0.9em' }}><RunScript lines={lines} asRun={asRun} /></div>}
            {d && <DrillDiagram drill={d.data} />}
            {edit && <div style={{ marginTop: 8, textAlign: 'right' }}><button className="btn btn-sm" onClick={edit}>Edit drill</button></div>}
          </div>
        );
      })}
      {assignments.length === 0 && <div className="empty">Nothing assigned to this period.</div>}
    </TextZoom>
  );
}

function PeriodModal({ period, insert, startLock, minStart, hasLater, isFirst, defaultTime, defaultDuration, types, drills, scripts, canEditDrill, onClose, onSave, onEditDrill, onDelete }: {
  period: Period | null;
  /** Opened from a ＋ between periods. */
  insert?: boolean;
  /** Start can't be typed here (insert slot / add on the end); the reason, shown as a hint. */
  startLock?: string;
  /** Editing: the earliest start allowed (one minute after the period before begins). */
  minStart?: string | null;
  /** Editing a period that has periods after it. */
  hasLater?: boolean;
  isFirst?: boolean;
  defaultTime: string;
  defaultDuration: number;
  types: { code: string; label: string; bg: string; fg: string }[];
  drills: LibraryItemRow<DrillData>[];
  onClose: () => void;
  scripts: ScriptLib;
  canEditDrill: (d: LibraryItemRow<DrillData>) => boolean;
  onSave: (p: Period) => void;
  /** Save the period as typed, then open this drill in the drill editor. */
  onEditDrill: (p: Period, drillId: string) => void;
  onDelete?: () => void;
}) {
  const [preview, setPreview] = useState(false);
  const app = useApp();
  const [time, setTime] = useState(period ? fmt(period.time) : defaultTime);
  const [type, setType] = useState(period?.type ?? types[0]?.code ?? 'WU');
  const [duration, setDuration] = useState(defaultDuration);
  const [assignments, setAssignments] = useState<Assignment[]>(() => (period ? normalizeAssignments(period) : []));
  const confirm = useConfirm();
  // Plugin rule: the drill list is filtered to the period's type; WU / Water / EOWO show everything.
  const filtered = useMemo(() => (['WU', 'Water', 'EOWO'].includes(type) ? drills : drills.filter((d) => !d.data.type || d.data.type === type)), [drills, type]);
  const cats = useMemo(() => [...new Set(filtered.map((d) => d.data.cat || 'Other'))].sort(), [filtered]);

  const current = (): Period => ({ time, type, duration: Math.max(1, duration || 8), assignments });
  const startMoved = !!period && !!time && fmt(time) !== fmt(period.time);
  const minsChanged = !!period && Math.max(1, duration || 8) !== defaultDuration;
  const tooEarly = !!minStart && !!time && fmt(time) < minStart;
  const timeHint = tooEarly
    ? `⚠ Has to start at ${minStart} or later — after the period before it begins`
    : startMoved && isFirst
      ? 'The whole practice moves with this start'
      : startMoved && hasLater
        ? 'This period and everything after it move to the new start; the period before stretches to meet it'
        : startMoved
          ? 'The period before stretches to meet the new start'
          : minsChanged && hasLater
            ? 'Everything after this period slides to match'
            : '';
  const upd = (i: number, patch: Partial<Assignment>) => setAssignments((a) => a.map((x, j) => (j === i ? { ...x, ...patch } : x)));

  return (
    <Modal open onClose={onClose} title={period ? 'Edit period' : insert ? 'Insert period' : 'Add period'}
      actions={
        <>
          {onDelete && <button className="btn btn-danger" onClick={async () => { if (await confirm('Delete this period?', { ok: 'Delete', danger: true })) onDelete(); }}>Delete</button>}
          <span className="grow" />
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn" onClick={() => setPreview(true)} title="What coaches see in Run mode">👁 Preview</button>
          <button className="btn btn-primary" disabled={tooEarly} onClick={() => onSave(current())}>Save</button>
        </>
      }
    >
      <Modal open={preview} onClose={() => setPreview(false)} closeButton title={`${fmt(time)} · ${types.find((t) => t.code === type)?.label ?? type}`}
        actions={<><span className="muted grow" style={{ fontSize: 12 }}>Preview — what coaches see in Run</span><button className="btn btn-primary" onClick={() => setPreview(false)}>Back to editing</button></>}>
        {preview && <PeriodSheet assignments={assignments} drills={drills} scripts={scripts} editDrill={(d) => (canEditDrill(d) ? () => onEditDrill(current(), d.id) : undefined)} />}
      </Modal>
      <div className="row" style={{ gap: 8, alignItems: 'flex-start' }}>
        <div className="field grow"><label>Start</label><input type="time" value={time} min={minStart ?? undefined} disabled={!!startLock} title={startLock} onChange={(e) => setTime(e.target.value)} /></div>
        <div className="field grow"><label>Minutes</label><input type="number" min={1} max={180} value={duration} onChange={(e) => setDuration(parseInt(e.target.value, 10) || 0)} /></div>
      </div>
      {(timeHint || startLock) && <div className={'small period-time-hint' + (tooEarly ? ' save-error' : '')} style={{ margin: '-4px 0 8px', opacity: tooEarly ? 1 : 0.8 }}>{timeHint || startLock}</div>}
      <div className="field">
        <label>Type</label>
        <select value={type} onChange={(e) => setType(e.target.value)}>
          {types.map((t) => <option key={t.code} value={t.code}>{t.label}</option>)}
        </select>
      </div>
      <div className="section-title" style={{ margin: '8px 0 6px' }}>Assignments</div>
      {assignments.map((a, i) => (
        <div key={i} className="card" style={{ margin: '0 0 8px', padding: 10 }}>
          <div className="row">
            <select className="grow" value={a.drillId} onChange={(e) => upd(i, { drillId: e.target.value, customActivity: e.target.value ? '' : a.customActivity })}>
              <option value="">— custom activity —</option>
              {cats.map((c) => (
                <optgroup key={c} label={c}>
                  {filtered.filter((d) => (d.data.cat || 'Other') === c).map((d) => <option key={d.id} value={d.id}>{d.data.name}</option>)}
                </optgroup>
              ))}
            </select>
            <button className="btn btn-sm" onClick={() => setAssignments((x) => x.filter((_, j) => j !== i))} title="Remove">✕</button>
          </div>
          {!a.drillId && <input style={{ marginTop: 6 }} placeholder="Custom activity" value={a.customActivity} onChange={(e) => upd(i, { customActivity: e.target.value })} />}
          <div style={{ marginTop: 6 }}>
            <ChipToggle items={withCurrent(teamGroups(app.settings), a.groups)} selected={a.groups} onToggle={(g) => upd(i, { groups: a.groups.includes(g) ? a.groups.filter((x) => x !== g) : [...a.groups, g] })} />
          </div>
          <textarea style={{ marginTop: 6 }} rows={2} placeholder="Notes" value={a.notes} onChange={(e) => upd(i, { notes: e.target.value })} />
        </div>
      ))}
      <button className="btn btn-sm btn-green" onClick={() => setAssignments((x) => [...x, { drillId: '', customActivity: '', groups: [], notes: '' }])}>＋ Add assignment</button>
    </Modal>
  );
}
